import { CovideoGTMVehicleItem } from './types';
import { mapToGtmVehicleAttributes } from './utils';
import { inventoryInteractionEvent } from './actions/inventory/interaction';
import {
  createMeetingEvent,
  meetingInteractionEvent,
  scheduleMeetingInteractionEvent,
} from './actions/meeting';
import {
  editAttributesInteractionFormsEvent,
  editVideoAttributeInteractionActionEvent,
  saveNewVideoInteractionEvent,
  videoInteractonEvent,
  videoMessageInteractionEvent,
} from './actions/video';
import { useCallback } from 'react';
import {
  GTMSaveNewVideoInteractionEventArguments,
  GTMVideoInteractonEventArguments,
  GTMVideoMessageInteractionActions,
} from './actions/video/types';
import { useCDK } from '../useCdk';
import {
  selectRecordingTypeEvent,
  recordingToolIntearctionEvent,
  stopRecordingEvent,
  startRecordingEvent,
  addVideoAttributeEvent,
  discardVideoEvent,
  recordingSettingsChangedInteractionEvent,
} from './actions/recording';
import {
  myProfileChangedInteractionEvent,
  recentlyDeletedInteractionEvent,
} from './actions/profile';
import { newLeadAddedEvent } from './actions/customers';
import { teleprompterSettingsInteractionEvent } from './actions/teleprompter';
import {
  actionsDesingSettingsChangedEvent,
  designSettingsFieldsChangedEvent,
} from './actions/design';
import { customAttributeInteractionsEvent } from './actions/customAttribute/interaction';
import { emailSettingsChangedEvent } from './actions/email';
import { videoPlayerInteractionsEvent } from './actions/videoPlayer';
import { meetingAttributesChangedEvent } from './actions/meeting/attributesChanged';

type TrackVideoInteractionArgs = GTMVideoInteractonEventArguments & {
  vehicle?: CovideoGTMVehicleItem;
};

type TrackGTMVideoMessageInteractionEvent =
  GTMVideoMessageInteractionActions & {
    vehicle?: CovideoGTMVehicleItem;
  };

type TrackGTMFieldsInteractionEvent = {
  updatedFields: string[];
  fieldChanges?: Record<string, any>;
};

export function useGTMAnalytics() {
  const { isCdkUser } = useCDK();

  const trackVideoInteractionEvent = useCallback(
    async ({ vehicle, ...rest }: TrackVideoInteractionArgs) => {
      // WithIds variant: send just IDs
      if (Array.isArray(rest.videoIds) && rest.videoIds.length > 0) {
        return videoInteractonEvent({
          action: rest.action,
          videoIds: rest.videoIds,
        });
      }

      const vehicleData = mapToGtmVehicleAttributes(
        vehicle as CovideoGTMVehicleItem
      );

      // Details variant: requires videoAttributes + vehicleData
      return videoInteractonEvent({
        action: rest.action,
        videoAttributes: rest.videoAttributes,
        vehicleData,
      });
    },
    []
  );

  const trackSaveNewVideo = useCallback(
    async (newVideoArgs: GTMSaveNewVideoInteractionEventArguments) => {
      return saveNewVideoInteractionEvent(newVideoArgs);
    },
    []
  );

  const trackVideoMessageInteractionEvent = useCallback(
    async ({ vehicle, ...rest }: TrackGTMVideoMessageInteractionEvent) => {
      const vehicleData = mapToGtmVehicleAttributes(
        vehicle as CovideoGTMVehicleItem
      );

      return videoMessageInteractionEvent({
        ...rest,
        vehicleData,
      });
    },
    []
  );

  const myProfileInteractionEvent = useCallback(
    async ({ updatedFields, fieldChanges }: TrackGTMFieldsInteractionEvent) => {
      return myProfileChangedInteractionEvent({
        updatedFields,
        fieldChanges,
      });
    },
    []
  );

  const recordingSettingsInteractionEvent = useCallback(
    async ({ updatedFields, fieldChanges }: TrackGTMFieldsInteractionEvent) => {
      return recordingSettingsChangedInteractionEvent({
        updatedFields,
        fieldChanges,
      });
    },
    []
  );

  return {
    trackGTMVideoInteractionEvent: isCdkUser
      ? trackVideoInteractionEvent
      : undefined,
    trackGTMVideoMessageInteractionEvent: isCdkUser
      ? trackVideoMessageInteractionEvent
      : undefined,
    trackGTMSaveNewVideoInteractionEvent: isCdkUser
      ? trackSaveNewVideo
      : undefined,
    trackGTMeditAttributesInteractionFormsEvent: isCdkUser
      ? editAttributesInteractionFormsEvent
      : undefined,
    trackGTMEditVideoAttributeInteractionActionEvent: isCdkUser
      ? editVideoAttributeInteractionActionEvent
      : undefined,

    // inventory
    trackGTMInventoryInteractionEvent: isCdkUser
      ? inventoryInteractionEvent
      : undefined,

    // meeting
    trackGTMMeetingInteractionEvent: isCdkUser
      ? meetingInteractionEvent
      : undefined,
    trackGTMMeetingAttributesChangedEvent: isCdkUser
      ? meetingAttributesChangedEvent
      : undefined,
    trackGTMScheduleMeetingInteractionEvent: isCdkUser
      ? scheduleMeetingInteractionEvent
      : undefined,
    trackGTMCreateMeetingEvent: isCdkUser ? createMeetingEvent : undefined,

    // recording
    trackGTMRecordingToolIntearctionEvent: isCdkUser
      ? recordingToolIntearctionEvent
      : undefined,
    trackGTMSelectRecordingTypeEvent: isCdkUser
      ? selectRecordingTypeEvent
      : undefined,

    trackGTMStopRecordingEvent: isCdkUser ? stopRecordingEvent : undefined,
    trackGTMStartRecordingEvent: isCdkUser ? startRecordingEvent : undefined,
    trackGTMAddVideoAttributeEvent: isCdkUser
      ? addVideoAttributeEvent
      : undefined,
    trackGTMDiscardVideoEvent: isCdkUser ? discardVideoEvent : undefined,
    // profile
    trackGTMRecentlyDeletedInteractionEvent: isCdkUser
      ? recentlyDeletedInteractionEvent
      : undefined,
    // edit profile
    trackGTMMyProfileInteractionEvent: isCdkUser
      ? myProfileInteractionEvent
      : undefined,
    //customers
    trackGTMNewLeadAddedEvent: isCdkUser ? newLeadAddedEvent : undefined,

    // desing
    trackGTMDesignActionsSettingsChangedEvent: isCdkUser
      ? actionsDesingSettingsChangedEvent
      : undefined,

    // custom video attributes
    trackGTMCustomAttributeInteractionsEvent: isCdkUser
      ? customAttributeInteractionsEvent
      : undefined,

    // email
    trackGTMEmailSettingsChangedEvent: isCdkUser
      ? emailSettingsChangedEvent
      : undefined,

    // teleprompter settings
    trackGTMTeleprompterSettingsInteractionEvent: isCdkUser
      ? teleprompterSettingsInteractionEvent
      : undefined,

    // video player settings
    trackGTMVideoPlayerInteractionsEvent: isCdkUser
      ? videoPlayerInteractionsEvent
      : undefined,

    trackGTMDesignSettingsFieldsChangedEvent: isCdkUser
      ? designSettingsFieldsChangedEvent
      : undefined,

    trackGTMRecordingSettingsInteractionEvent: isCdkUser
      ? recordingSettingsInteractionEvent
      : undefined,
  };

  // design settings changes
}
